import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Box
} from '@mui/material'
import { replayPayment } from '../../../../../../../lib/requests.es6'

const MIN_REASON_LENGTH = 5

// Re-submits a Failed payment for allocation to its policy. Replaying credits
// the policy, so a reason (for example the evidence that the money was
// collected) is mandatory and is stored with the payment by the API.
export default function ReplayPayment(props) {
  const { open, onClose, payment, onSuccess } = props

  const [reason, setReason] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [success, setSuccess] = React.useState(false)

  const handleClose = () => {
    if (loading) return
    setReason('')
    setError(null)
    setSuccess(false)
    if (onClose) onClose()
  }

  const handleSubmit = async () => {
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(
        `Please provide a reason of at least ${MIN_REASON_LENGTH} characters`
      )
      return
    }

    setLoading(true)
    setError(null)

    try {
      await replayPayment({ paymentGuid: payment.guid, reason: reason.trim() })
      setLoading(false)
      setSuccess(true)
      if (onSuccess) onSuccess()
    } catch (e) {
      setLoading(false)
      let message = 'An unexpected error occurred'
      if (e.response && e.response.data) {
        if (e.response.data.details && e.response.data.details.message) {
          message = e.response.data.details.message
        } else if (e.response.data.message) {
          message = Array.isArray(e.response.data.message)
            ? e.response.data.message.join(', ')
            : e.response.data.message
        } else if (e.response.data.error) {
          message = e.response.data.error
        }
      }
      setError(message)
    }
  }

  if (!payment) return null

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Replay Payment</DialogTitle>
      <DialogContent>
        {success ? (
          <Box sx={{ textAlign: 'center', padding: '20px 0' }}>
            <Typography variant="h6" style={{ color: '#155724' }}>
              Payment submitted for allocation
            </Typography>
            <Typography variant="body2" style={{ marginTop: '8px' }}>
              The policy balance and status update once the payment has been
              allocated.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" style={{ marginBottom: '6px' }}>
              Payment Reference:{' '}
              <strong>{payment.mno_reference || payment.reference}</strong>
            </Typography>
            <Typography variant="body2" style={{ marginBottom: '15px' }}>
              This re-submits the failed payment so it is applied to the policy.
              Only do this once you have confirmed the money was collected.
            </Typography>
            <TextField
              autoFocus
              label="Reason for replaying this payment"
              fullWidth
              multiline
              rows={4}
              value={reason}
              onChange={e => setReason(e.target.value)}
              disabled={loading}
            />
            {error && (
              <Typography
                variant="body2"
                style={{ color: '#721c24', marginTop: '10px' }}
              >
                {error}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {success ? (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? 'Replaying...' : 'Replay Payment'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
